import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Loader2, Plus, Save, X } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { errMsg } from "@/lib/api";
import { useRootsExtras } from "@/components/RootsExtras";

const blank = () => ({ name: "", why: "", ideas: ["", "", ""] });
const fromExtras = (x) => {
  const rows = x.pillars.map((name, i) => {
    const d = x.pillar_sheet?.[i] || {};
    return { name, why: d.why || "", ideas: [0, 1, 2].map((j) => d.ideas?.[j] || "") };
  });
  while (rows.length < 3) rows.push(blank());
  return rows;
};

const PillarCard = ({ row, i, onChange, onRemove }) => (
  <div className="rounded-xl border border-border bg-white p-5 space-y-3" data-testid={`ws-pillar-${i}`}>
    <div className="flex items-center gap-3">
      <span className="font-serif text-2xl text-pinkb w-6">{i + 1}</span>
      <Input value={row.name} onChange={(e) => onChange({ name: e.target.value.slice(0, 120) })} placeholder="Pillar name, e.g. Behind the scenes" data-testid={`ws-name-${i}`} className="font-medium" />
      {onRemove && <button type="button" onClick={onRemove} aria-label="Remove pillar" data-testid={`ws-remove-${i}`} className="text-muted-foreground hover:text-rose-600"><X className="h-4 w-4" /></button>}
    </div>
    <div>
      <label className="text-xs font-mono uppercase text-muted-foreground">Why it matters to your customer</label>
      <Textarea value={row.why} onChange={(e) => onChange({ why: e.target.value.slice(0, 500) })} rows={2} placeholder="What does this show them, or why would they care?" data-testid={`ws-why-${i}`} className="mt-1" />
    </div>
    <div className="space-y-2">
      <label className="text-xs font-mono uppercase text-muted-foreground">3 post ideas</label>
      {row.ideas.map((t, j) => (
        <Input key={j} value={t} onChange={(e) => onChange({ ideas: row.ideas.map((v, k) => (k === j ? e.target.value.slice(0, 200) : v)) })} placeholder={`Post idea ${j + 1}`} data-testid={`ws-idea-${i}-${j}`} />
      ))}
    </div>
  </div>
);

export const PillarWorksheet = () => {
  const [x, save] = useRootsExtras();
  const [rows, setRows] = useState(null);
  const [busy, setBusy] = useState(false);
  const key = x ? JSON.stringify([x.pillars, x.pillar_sheet]) : "";
  useEffect(() => { if (x) setRows(fromExtras(x)); }, [key]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!rows) return <p className="text-sm text-muted-foreground">Loading your worksheet…</p>;
  const named = rows.filter((r) => r.name.trim());
  const update = (i, patch) => setRows((l) => l.map((r, j) => (j === i ? { ...r, ...patch } : r)));
  const submit = async () => {
    setBusy(true);
    try { await save({ pillars: named.map((r) => r.name), pillar_sheet: named.map((r) => ({ why: r.why, ideas: r.ideas })) }); }
    catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <div data-testid="pillar-worksheet">
      <p className="text-sm text-muted-foreground">Content pillars are the 3 to 5 themes you post about again and again. Name each one, say why it matters to your customer, and jot down three post ideas. It saves straight to My Roots Profile, so Lindsay's answers use it too.</p>
      <div className="mt-5 space-y-4">
        {rows.map((r, i) => <PillarCard key={i} row={r} i={i} onChange={(p) => update(i, p)} onRemove={rows.length > 3 ? () => setRows((l) => l.filter((_, j) => j !== i)) : null} />)}
      </div>
      <div className="mt-5 flex flex-wrap items-center justify-between gap-3">
        {rows.length < 5 ? <button type="button" onClick={() => setRows((l) => [...l, blank()])} data-testid="ws-add" className="inline-flex items-center gap-1 text-sm text-pinkb hover:underline"><Plus className="h-4 w-4" />Add a pillar</button> : <span />}
        <Button onClick={submit} disabled={busy || named.length < 3} data-testid="ws-save" className="rounded-full bg-navy hover:bg-navy/90 text-white">
          {busy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}Save to My Roots Profile
        </Button>
      </div>
      {named.length < 3 && <p className="mt-2 text-xs text-muted-foreground" data-testid="ws-hint">Name at least 3 pillars to save.</p>}
      <Link to="/app/foundations#marketing-foundations" data-testid="ws-profile-link" className="mt-4 inline-flex items-center gap-1 text-sm text-pinkb hover:underline">See it in My Roots Profile <ArrowRight className="h-3 w-3" /></Link>
    </div>
  );
};
